import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { UserRole } from '../../core/enums/user-role.enum';
import {
  PassengerOnboardingLookupResult,
  PassengerOnboardingService,
  PassengerOnboardingStartResult
} from '../../core/services/passenger-onboarding.service';

@Component({
  selector: 'app-auth',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.css']
})
export class AuthComponent implements OnInit {
  private fb = inject(FormBuilder);
  isRegisterMode = false;
  isLoading = false;
  errorMessage = '';
  successMessage = '';
  passengerOnboardingStep: 'contact' | 'confirm' | 'manualEmail' | 'existingUser' | 'queued' | 'notFound' | 'manualRegistration' | 'verificationIssue' = 'contact';
  passengerLookupResult: PassengerOnboardingLookupResult | null = null;
  passengerStartResult: PassengerOnboardingStartResult | null = null;

  loginForm = this.fb.group({
    email: ['', [Validators.required]],
    password: ['', Validators.required],
    rememberMe: [true]
  });

  registerForm = this.fb.group({
    userName: ['', Validators.required],
    userEmail: ['', [Validators.required, Validators.email]],
    phoneNumber: [''],
    userPassword: ['', [Validators.required, Validators.minLength(6)]],
    userRole: [UserRole.PASSENGER, Validators.required],
    organisationName: [''],
    organisationPhone: [''],
    organisationWebsite: ['', Validators.pattern(/^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/i)],
    organisationAddress: [''],
    acceptTerms: [false, Validators.requiredTrue]
  });

  passengerOnboardingForm = this.fb.group({
    contact: ['', Validators.required],
    manualEmail: ['', [Validators.email]],
    acceptTerms: [false, Validators.requiredTrue]
  });

  constructor(
    private authService: AuthService,
    private passengerOnboardingService: PassengerOnboardingService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.isRegisterMode = this.router.url.includes('/register');
    if (this.route.snapshot.queryParamMap.get('sessionExpired') === 'true') {
      this.isRegisterMode = false;
      this.errorMessage = 'Your session expired due to inactivity. Please sign in again.';
    }

    this.registerForm.get('userRole')?.valueChanges.subscribe(() => this.updateRegistrationValidators());
    this.updateRegistrationValidators();
  }

  onLogin(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    const { email, password } = this.loginForm.getRawValue();

    this.authService.login(email || '', password || '').subscribe({
      next: () => {
        const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || '/dashboard';
        this.router.navigateByUrl(returnUrl);
      },
      error: error => {
        console.error('Login failed:', error);
        this.errorMessage = this.getErrorMessage(error, 'Login failed. Please check your username and password.');
        this.isLoading = false;
      }
    });
  }

  onRegister(): void {
    if (this.registerForm.get('userRole')?.value === UserRole.PASSENGER) {
      this.lookupPassengerContact();
      return;
    }

    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();
      this.errorMessage = this.getRegistrationValidationMessage();
      this.successMessage = '';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    const formValue = this.registerForm.getRawValue();

    this.authService.register({
      userName: formValue.userName || '',
      userEmail: formValue.userEmail || '',
      phoneNumber: formValue.phoneNumber || undefined,
      userPassword: formValue.userPassword || '',
      userRole: formValue.userRole || UserRole.PASSENGER,
      organisationName: formValue.organisationName || undefined,
      organisationWebsite: this.normalizeWebsiteUrl(formValue.organisationWebsite || ''),
      organisationPhone: formValue.organisationPhone || undefined,
      organisationAddress: formValue.organisationAddress || undefined
    }).subscribe({
      next: () => {
        this.successMessage = this.getRegistrationSuccessMessage(formValue.userRole || UserRole.PASSENGER);
        this.isRegisterMode = false;
        this.isLoading = false;
        this.loginForm.patchValue({
          email: formValue.userEmail || '',
          password: ''
        });
      },
      error: error => {
        console.error('Registration failed:', error);
        this.errorMessage = this.getErrorMessage(error, 'Registration failed. Please review the details and try again.');
        this.isLoading = false;
      }
    });
  }

  lookupPassengerContact(): void {
    const contact = this.passengerOnboardingForm.get('contact')?.value?.trim() || '';

    if (!contact) {
      this.errorMessage = 'Please enter your cellphone number or email address.';
      this.successMessage = '';
      this.passengerOnboardingForm.get('contact')?.markAsTouched();
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.passengerLookupResult = null;
    this.passengerStartResult = null;

    this.passengerOnboardingService.lookup(contact).subscribe({
      next: result => {
        this.passengerLookupResult = result;
        this.prefillPassengerFields(result);
        this.passengerOnboardingStep = this.getNextPassengerOnboardingStep(result);
        this.isLoading = false;
      },
      error: error => {
        console.error('Passenger onboarding lookup failed:', error);
        this.errorMessage = this.getErrorMessage(error, 'Unable to check that contact. Please try again.');
        this.isLoading = false;
      }
    });
  }

  startPassengerOnboarding(): void {
    const contact = this.passengerOnboardingForm.get('contact')?.value?.trim() || '';
    const manualEmail = this.passengerOnboardingForm.get('manualEmail')?.value?.trim() || '';
    const lookupEmail = this.getPrefillEmail();
    const emailForVerification = manualEmail || lookupEmail;

    if (this.passengerOnboardingForm.get('acceptTerms')?.invalid) {
      this.passengerOnboardingForm.get('acceptTerms')?.markAsTouched();
      this.errorMessage = 'Please accept the terms before continuing.';
      return;
    }

    if (!emailForVerification) {
      this.passengerOnboardingStep = 'manualEmail';
      this.errorMessage = 'Please enter an email address so we can send your verification link.';
      return;
    }

    if (manualEmail && this.passengerOnboardingForm.get('manualEmail')?.invalid) {
      this.passengerOnboardingForm.get('manualEmail')?.markAsTouched();
      this.errorMessage = 'Please enter a valid email address.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.registerForm.patchValue({ userEmail: emailForVerification });

    this.passengerOnboardingService.start(
      contact,
      this.getVerificationUrlBase(),
      manualEmail || undefined
    ).subscribe({
      next: result => {
        this.passengerStartResult = result;
        if (result.emailVerificationQueued) {
          this.passengerOnboardingStep = 'queued';
          this.successMessage = 'Verification email queued. Please check your inbox to continue.';
        } else if (!emailForVerification) {
          this.passengerOnboardingStep = 'manualEmail';
          this.successMessage = '';
          this.errorMessage = 'We found your details. Please add an email address so verification can continue.';
        } else {
          this.passengerOnboardingStep = 'verificationIssue';
          this.successMessage = result.userCreated
            ? 'Your passenger account was prepared, but the verification email was not queued. Please try again or contact support if it persists.'
            : 'Your details were found, but the verification email was not queued. Please try again or contact support if it persists.';
        }
        this.isLoading = false;
      },
      error: error => {
        console.error('Passenger onboarding start failed:', error);
        this.errorMessage = this.getErrorMessage(error, 'Unable to start passenger onboarding. Please try again.');
        this.isLoading = false;
      }
    });
  }

  resetPassengerOnboarding(): void {
    this.passengerOnboardingStep = 'contact';
    this.passengerLookupResult = null;
    this.passengerStartResult = null;
    this.errorMessage = '';
    this.successMessage = '';
    this.passengerOnboardingForm.patchValue({ manualEmail: '' });
  }

  continueManualPassengerRegistration(): void {
    const contact = this.passengerOnboardingForm.get('contact')?.value?.trim() || '';

    this.passengerOnboardingStep = 'manualRegistration';
    this.errorMessage = '';
    this.successMessage = '';
    this.registerForm.patchValue({
      userRole: UserRole.PASSENGER,
      userEmail: this.isEmail(contact) ? contact : this.registerForm.get('userEmail')?.value || '',
      phoneNumber: this.isEmail(contact) ? this.registerForm.get('phoneNumber')?.value || '' : contact
    });
  }

  submitManualPassengerRegistration(): void {
    this.registerForm.patchValue({ userRole: UserRole.PASSENGER });

    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();
      this.errorMessage = this.getRegistrationValidationMessage();
      this.successMessage = '';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    const formValue = this.registerForm.getRawValue();

    this.authService.register({
      userName: formValue.userName || '',
      userEmail: formValue.userEmail || '',
      phoneNumber: formValue.phoneNumber || undefined,
      userPassword: formValue.userPassword || '',
      userRole: UserRole.PASSENGER
    }).subscribe({
      next: () => {
        this.successMessage = this.getRegistrationSuccessMessage(UserRole.PASSENGER);
        this.isRegisterMode = false;
        this.isLoading = false;
        this.loginForm.patchValue({
          email: formValue.userEmail || '',
          password: ''
        });
      },
      error: error => {
        console.error('Passenger manual registration failed:', error);
        this.errorMessage = this.getErrorMessage(error, 'Registration failed. Please review the details and try again.');
        this.isLoading = false;
      }
    });
  }

  useExistingUserLogin(): void {
    const email = this.getExistingUserEmail() || this.getPrefillEmail() || this.passengerOnboardingForm.get('contact')?.value || '';
    this.loginForm.patchValue({ email, password: '' });
    this.isRegisterMode = false;
  }

  getPrefillName(): string {
    const prefill = this.passengerLookupResult?.prefill || this.passengerStartResult?.prefill;
    return prefill?.displayName
      || prefill?.fullName
      || prefill?.userName
      || [prefill?.firstName, prefill?.lastName].filter(Boolean).join(' ')
      || '';
  }

  getPrefillPhone(): string {
    const prefill = this.passengerLookupResult?.prefill || this.passengerStartResult?.prefill;
    return prefill?.phone || prefill?.phoneNumber || '';
  }

  getPrefillEmail(): string {
    const prefill = this.passengerLookupResult?.prefill || this.passengerStartResult?.prefill;
    return prefill?.email || prefill?.emailAddress || '';
  }

  getContactPhoneFallback(): string {
    const contact = this.passengerOnboardingForm.get('contact')?.value?.trim() || '';
    return this.isEmail(contact) ? '' : contact;
  }

  getContactEmailFallback(): string {
    const contact = this.passengerOnboardingForm.get('contact')?.value?.trim() || '';
    return this.isEmail(contact) ? contact : '';
  }

  getExistingUserEmail(): string {
    const user = this.passengerLookupResult?.existingUser;
    return user?.userEmail || user?.email || user?.username || '';
  }

  private getErrorMessage(error: any, fallback: string): string {
    if (typeof error?.error === 'string') {
      return error.error;
    }

    return error?.error?.message || error?.message || fallback;
  }

  private updateRegistrationValidators(): void {
    const role = this.registerForm.get('userRole')?.value;
    const organisationNameControl = this.registerForm.get('organisationName');

    if (role === UserRole.OPERATOR) {
      organisationNameControl?.setValidators([Validators.required]);
    } else {
      organisationNameControl?.clearValidators();
    }

    organisationNameControl?.updateValueAndValidity({ emitEvent: false });
  }

  private getRegistrationSuccessMessage(role: UserRole): string {
    if (role === UserRole.OPERATOR) {
      return 'Operator account created. A verification email has been sent, once verified we will contact you before activation.';
    }

    return 'Passenger account created. A verification email has been sent; sign-in is available once the account is verified.';
  }

  private getRegistrationValidationMessage(): string {
    const controls = this.registerForm.controls;

    if (controls.userName.invalid) {
      return 'Please enter your full name.';
    }

    if (controls.userEmail.invalid) {
      return 'Please enter a valid email address.';
    }

    if (controls.userPassword.invalid) {
      return 'Please enter a password with at least 6 characters.';
    }

    if (controls.userRole.value === UserRole.OPERATOR && controls.organisationName.invalid) {
      return 'Please enter the operator organisation name.';
    }

    if (controls.organisationWebsite.invalid) {
      return 'Please enter a valid company website or leave it blank.';
    }

    if (controls.acceptTerms.invalid) {
      return 'Please accept the terms before creating an account.';
    }

    return 'Please complete the required registration fields.';
  }

  private prefillPassengerFields(result: PassengerOnboardingLookupResult): void {
    const prefill = result.prefill;
    if (!prefill) {
      return;
    }

    this.registerForm.patchValue({
      userName: this.getPrefillName(),
      userEmail: this.getPrefillEmail(),
      phoneNumber: this.getPrefillPhone()
    });

    if (!this.passengerOnboardingForm.get('manualEmail')?.value && this.getPrefillEmail()) {
      this.passengerOnboardingForm.patchValue({ manualEmail: this.getPrefillEmail() });
    }
  }

  private getNextPassengerOnboardingStep(result: PassengerOnboardingLookupResult): typeof this.passengerOnboardingStep {
    if (result.existingUser) {
      return 'existingUser';
    }

    if (result.foundInSystem || result.foundInHotspot || result.canPrefill || result.prefill) {
      return 'confirm';
    }

    return 'notFound';
  }

  private isEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  private getVerificationUrlBase(): string {
    return `${window.location.origin}/verify-email`;
  }

  private normalizeWebsiteUrl(value: string): string | undefined {
    const website = value.trim();
    if (!website) {
      return undefined;
    }

    return /^https?:\/\//i.test(website) ? website : `https://${website}`;
  }
}
